import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { createEvent, deleteEvent } from '../events/slice';
import { captureHydrationException } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  EventQuota,
  EventQuotaSerializedEntity,
  EventQuotasState,
} from './types';

export const eventQuotasAdapter = createEntityAdapter<EventQuota>();

export const initialState: EventQuotasState = {
  ...eventQuotasAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: eventQuotasAdapter.setAll,
    removeAll: eventQuotasAdapter.removeAll,
  },
  extraReducers: builder => {
    builder
      .addCase(
        Schedule.resourceActionEvent('fetch'),
        (state: EventQuotasState, action: ScheduleFetchAction) => {
          state.status = 'succeeded';
          if (action.payload?.included) {
            try {
              const eventQuotas = (
                action?.payload?.included as EventQuotaSerializedEntity[]
              )
                .filter(
                  (entity: EventQuotaSerializedEntity) =>
                    entity.type === 'eventQuota'
                )
                .map((entity: EventQuotaSerializedEntity) => ({
                  id: entity.id,
                  unlimited: entity.attributes.unlimited,
                  month: entity.attributes.month,
                  year: entity.attributes.year,
                  used: entity.attributes.used,
                  allowed: entity.attributes.allowed,
                }));
              eventQuotasAdapter.setAll(state, eventQuotas);
            } catch (error) {
              captureHydrationException(state, error, SLICE_NAME);
            }
          }
        }
      )
      .addCase(createEvent.fulfilled, (state, action) => {
        if (action.payload.included && action.payload.included.length > 0) {
          const eventQuota = action.payload.included[0];

          const updatedEventQuota = {
            id: eventQuota.id,
            unlimited: eventQuota.attributes.unlimited,
            month: eventQuota.attributes.month,
            year: eventQuota.attributes.year,
            used: eventQuota.attributes.used,
            allowed: eventQuota.attributes.allowed,
          } as EventQuota;

          eventQuotasAdapter.updateOne(state, {
            id: eventQuota.id,
            changes: updatedEventQuota,
          });
        }

        state.status = 'succeeded';
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const updatedEventQuota: EventQuota = {
          id: action.payload.id,
          unlimited: action.payload.attributes.unlimited,
          month: action.payload.attributes.month,
          year: action.payload.attributes.year,
          used: action.payload.attributes.used,
          allowed: action.payload.attributes.allowed,
        };

        eventQuotasAdapter.updateOne(state, {
          id: action.payload.id,
          changes: updatedEventQuota,
        });

        state.status = 'succeeded';
      });
  },
});

export const { selectAll, selectById } =
  eventQuotasAdapter.getSelectors<RootState>(
    state => state.get('scheduleBuilderSchedule').eventQuotas
  );

export const { reducer, actions } = slice;
